import { useContext } from 'react'

import { useLatestEntity } from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'

import launchModal from '@functions/utils/launchModal'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import * as adAccountGroupLinkActions from '@redux/modules/adAccountGroupLink'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams } from '@redux/modules/types'
import type { AdAccountGroupLinkModel } from '@models/types'

type BulkCreateAdAccountGroupLinksParams = {
  bulkCreateParams: { adAccountGroupIds: number[], linkableIds: number[], linkableType: string },
  dispatch: AppDispatch,
}

const bulkCreateAdAccountGroupLinks = (params: BulkCreateAdAccountGroupLinksParams) => {
  const { bulkCreateParams, dispatch } = params
  const { bulkCreateAdAccountGroupLinks: bulkCreateFn } = adAccountGroupLinkActions

  return dispatch(bulkCreateFn(bulkCreateParams))
}

type DeleteAdAccountGroupLinkParams = {
  adAccountGroupLink: DeleteParams<AdAccountGroupLinkModel>,
  dispatch: AppDispatch,
}

const deleteAdAccountGroupLink = (params: DeleteAdAccountGroupLinkParams) => {
  const { dispatch, adAccountGroupLink } = params
  const { deleteAdAccountGroupLink: deleteFn } = adAccountGroupLinkActions

  return dispatch(deleteFn(adAccountGroupLink))
}

export const useRelations = (adAccountGroupLink: Partial<AdAccountGroupLinkModel> = {}) => {
  const { linkableId, linkableType } = adAccountGroupLink

  const { clients, users } = useSelector(reduxState => reduxState.entities)

  let linkableEntity = {}
  if (!!linkableId && !!linkableType) {
    switch (linkableType) {
      case 'Client':
        linkableEntity = clients[linkableId] || {}
        break
      case 'User':
        linkableEntity = users[linkableId] || {}
        break
    }
  }

  return {
    linkableEntity,
  }
}

function useAdAccountGroupLink(initEntity: Partial<AdAccountGroupLinkModel> = {}) {
  const { entity: adAccountGroupLink }: { entity: AdAccountGroupLinkModel } = useLatestEntity(initEntity, 'adAccountGroupLinks')

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const {
    creating, deleting, loading,
  } = useSelector(reduxState => reduxState.adAccountGroupLinks)

  return {
    adAccountGroupLink,
    callbacks: {
      bulkCreateAdAccountGroupLinks: (bulkCreateParams: BulkCreateAdAccountGroupLinksParams['bulkCreateParams']) => (
        bulkCreateAdAccountGroupLinks({ bulkCreateParams, dispatch })
      ),
      deleteAdAccountGroupLink: () => deleteAdAccountGroupLink({ adAccountGroupLink, dispatch }),
      launchBulkCreateAdAccountGroupLinksModal: () => launchModal({
        callbacks,
        modalKey: 'BulkCreateAdAccountGroupLinksModal',
        payload: { adAccountGroupLink },
      }),
    },
    creating,
    deleting,
    loading,
  }
}

export default useAdAccountGroupLink
